import { CPLAT_API_ROUTES, fetcher, usePost } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { MembershipReviewPreviewTutorialProvider } from '@/components/membership/MembershipReviewPreviewTutorial';
import { useAppSelector } from '@/redux/store';
import type {
  ReviewCommentTemplate,
  ReviewCommentTemplateParams,
  ReviewCommentTemplateResponse,
  StoreListData,
} from '@/types/api';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type MarketStoreListItem, SELECT_ALL_MARKET } from '../../_consts/constants';
import { type TabKey, DEFAULT_TAB } from '../../_consts/reviewsConstants';
import { getMarketKey } from '../../_utils/getMarketKey';
import {
  membershipReviewInquiryPreviewDates,
  membershipReviewInquiryPreviewMarkets,
  membershipReviewPreviewStoreListResponse,
  membershipReviewPreviewTemplateResponse,
} from '../membership-review-inquiry-mock';
import { useSuspenseQuery } from '@tanstack/react-query';

interface ReviewsContextType {
  // 유저가 갖고 있는 마켓 리스트
  marketList: MarketStoreListItem[];

  // 실제 선택된 마켓 객체
  selectedMarket: MarketStoreListItem | null;
  setSelectedMarket: (market: MarketStoreListItem) => void;

  // 유효한 마켓 리스트 (MarketId가 0이 아닌 마켓들 - 전체 제외)
  validMarkets: MarketStoreListItem[];

  currentTab: TabKey;
  setCurrentTab: (tab: TabKey) => void;
  selectedMarketId: string | null;
  setSelectedMarketId: (marketId: string | null) => void;

  startDate: Date | undefined;
  endDate: Date | undefined;
  handleStartDate: (date: Date | undefined) => void;
  handleEndDate: (date: Date | undefined) => void;

  // 필터 상태
  filterSelect: { label: string; value: string };
  setFilterSelect: React.Dispatch<React.SetStateAction<{ label: string; value: string }>>;
  //리뷰 템플릿 목록 상태
  templateList: ReviewCommentTemplate[];
  refetchTemplateList: () => void;
  handleAddOrEditTemplate: (params: ReviewCommentTemplateParams, onSuccess?: () => void) => void;
  handleDeleteTemplate: (params: ReviewCommentTemplateParams, onSuccess?: () => void) => void;
  isTemplateSaving: boolean;
  isTemplateDeleting: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}
const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined);

export const RevieswsProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = (searchParams.get('ctab') as TabKey) || DEFAULT_TAB;
  const selectedMarketIdFromUrl = searchParams.get('select') || null;

  const [currentTab, setCurrentTab] = useState<TabKey>(tabFromUrl);
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(selectedMarketIdFromUrl);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 템플릿 목록 상태
  const [templateList, setTemplateList] = useState<ReviewCommentTemplate[]>([]);

  // 템플릿 목록 불러오기
  const { data: templateData, refetch: refetchTemplateList } =
    useSuspenseQuery<ReviewCommentTemplateResponse>({
      queryKey: [
        isPreviewMode
          ? 'membershipReviewPreviewTemplate'
          : CPLAT_API_ROUTES.getReviewCommentTemplate,
        { cplatToken: user.CplatToken },
      ],
      queryFn: () => {
        if (isPreviewMode) return membershipReviewPreviewTemplateResponse;

        return fetcher<ReviewCommentTemplateResponse>({
          queryKey: [CPLAT_API_ROUTES.getReviewCommentTemplate, { cplatToken: user.CplatToken }],
          method: 'POST',
        });
      },
    });

  useEffect(() => {
    if (templateData?.data?.[0]?.ReviewCommentTemplateList) {
      setTemplateList(templateData.data[0].ReviewCommentTemplateList);
    } else {
      setTemplateList([]);
    }
  }, [templateData]);

  // 템플릿 추가/수정
  const { mutate: addOrEditTemplate, isPending: isTemplateSaving } = usePost(
    CPLAT_API_ROUTES.setReviewCommentTemplate,
  );
  // 템플릿 추가/수정
  const handleAddOrEditTemplate = (params: ReviewCommentTemplateParams, onSuccess?: () => void) => {
    addOrEditTemplate(
      {
        ...params,
        cplatToken: user.CplatToken, // 항상 토큰 포함
      },
      {
        onSuccess: () => {
          refetchTemplateList();
          onSuccess?.();
        },
      },
    );
  };

  // 템플릿 삭제
  const { mutate: deleteTemplate, isPending: isTemplateDeleting } = usePost(
    CPLAT_API_ROUTES.deleteReviewCommentTemplate,
  );

  const handleDeleteTemplate = (params: ReviewCommentTemplateParams, onSuccess?: () => void) => {
    deleteTemplate(
      {
        ...params,
        cplatToken: user.CplatToken, // 항상 토큰 포함
      },
      {
        onSuccess: () => {
          refetchTemplateList();
          onSuccess?.();
        },
      },
    );
  };

  const { data: registeredMarketRes } = useSuspenseQuery<StoreListData>({
    queryKey: [
      isPreviewMode ? 'membershipReviewPreviewStoreList' : CPLAT_API_ROUTES.getStoreList,
      { cplatToken: user.CplatToken },
    ],
    queryFn: () => {
      if (isPreviewMode) return membershipReviewPreviewStoreListResponse;

      return fetcher<StoreListData>({
        queryKey: [CPLAT_API_ROUTES.getStoreList, { cplatToken: user.CplatToken }],
        method: 'POST',
      });
    },
  });

  const marketList = useMemo(() => {
    if (isPreviewMode) return membershipReviewInquiryPreviewMarkets;

    const storeList = registeredMarketRes?.data?.[0]?.StoreList;
    if (!storeList) return [];
    return [SELECT_ALL_MARKET, ...storeList];
  }, [isPreviewMode, registeredMarketRes]);

  // 실제 선택된 마켓 객체
  const selectedMarket = useMemo(() => {
    if (!selectedMarketId) return marketList[0] ?? null;
    return (
      marketList.find(
        (m) => getMarketKey({ marketId: m.MarketId, storeId: m.StoreId }) === selectedMarketId,
      ) ??
      marketList[0] ??
      null
    );
  }, [marketList, selectedMarketId]);

  const setSelectedMarket = (market: MarketStoreListItem) => {
    setSelectedMarketId(getMarketKey({ marketId: market.MarketId, storeId: market.StoreId }));
  };

  // 유효한 마켓 리스트 (MarketId가 0이 아닌 마켓들 - 전체 제외)
  const validMarkets = useMemo(() => marketList.filter((m) => m.MarketId !== 0), [marketList]);

  //  날짜 상태 추가
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    if (isPreviewMode) return membershipReviewInquiryPreviewDates.startDate;

    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    return sevenDaysAgo;
  });

  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    isPreviewMode ? membershipReviewInquiryPreviewDates.endDate : new Date(),
  );

  // 날짜 핸들러 추가
  const handleStartDate = (date: Date | undefined) => {
    if (date && endDate && date > endDate) {
      setStartDate(date);
      setEndDate(date);
    } else {
      setStartDate(date);
    }
  };

  const handleEndDate = (date: Date | undefined) => {
    if (date && startDate && date < startDate) {
      setStartDate(date);
      setEndDate(date);
    } else {
      setEndDate(date);
    }
  };

  const [filterSelect, setFilterSelect] = useState<{ label: string; value: string }>({
    label: '최신순',
    value: 'latest',
  });
  // latest, oldest, highRating, lowRating

  // 쿼리와 상태 동기화
  useEffect(() => {
    setSearchParams(
      {
        ctab: currentTab,
        select: selectedMarket
          ? getMarketKey({ marketId: selectedMarket.MarketId, storeId: selectedMarket.StoreId })
          : '',
      },
      { replace: true },
    );
    // eslint-disable-next-line
  }, [currentTab, selectedMarket]);

  // 쿼리 변경 시 상태도 동기화
  useEffect(() => {
    if (tabFromUrl !== currentTab) setCurrentTab(tabFromUrl);
    if (selectedMarketIdFromUrl !== selectedMarketId) setSelectedMarketId(selectedMarketIdFromUrl);
    // eslint-disable-next-line
  }, [tabFromUrl, selectedMarketIdFromUrl]);

  const value = {
    // 유저가 갖고 있는 마켓 리스트
    marketList,
    // 실제 선택된 마켓 객체
    selectedMarket,
    setSelectedMarket,

    // 유효한 마켓 리스트 (MarketId가 0이 아닌 마켓들 - 전체 제외)
    validMarkets,

    currentTab,
    setCurrentTab,
    selectedMarketId,
    setSelectedMarketId,

    startDate,
    endDate,
    handleStartDate,
    handleEndDate,

    // 필터 상태
    filterSelect,
    setFilterSelect,

    // 리뷰 템플릿 목록 상태
    templateList,
    refetchTemplateList,
    handleAddOrEditTemplate,
    handleDeleteTemplate,
    isTemplateSaving,
    isTemplateDeleting,
    scrollContainerRef,
  };

  return (
    <ReviewsContext.Provider value={value}>
      <MembershipReviewPreviewTutorialProvider>{children}</MembershipReviewPreviewTutorialProvider>
    </ReviewsContext.Provider>
  );
};

export const useReviews = () => {
  const context = useContext(ReviewsContext);
  if (!context) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }
  return context;
};
